import { IonCard, IonCol, IonGrid, IonItem, IonRow } from "@ionic/react";
import { Page } from "components/Page";
import { useDeviceConnection } from "functions/Context";
import { FormatNumber, FormatNumberSI } from "functions/Formatting";
import { Glob, Loc, useLoc } from "functions/Language";
import { GCR12_Alarms, TYPE_GCR, TYPE_GCRT, TYPE_ICR } from "functions/networking/NetTypes";
import { useLoader } from "functions/networking/useLoader";
import { warningOutline } from "ionicons/icons";
import { DvcPage } from "navigation/DvcPage";
import { RouterMap } from "navigation/RouterMap";
import { FC } from "react";

const route = RouterMap.alarms;

const alarm_units = {
	ul : "V",
	uh : "V",
	il : "A",
	ih : "A",
	co : "",
	hu : "%",
	hi : "%",
	te : "°C",
	rs : "",
} as const;

function CheckAlType(typ: string) : typ is keyof typeof alarm_units {
	if (!Object.keys(alarm_units).includes(typ)) console.error("Unhandled alarm type", typ);
	return true;
}

const AlarmDisplay : FC<{
	data: GCR12_Alarms[number],
	loc: Loc<typeof glob>,
	showPhase: boolean,
}> = (props) => {
	if (!CheckAlType(props.data.typ)) return <></>;
	const unit = alarm_units[props.data.typ];
	const si = unit === "V" || unit === "A";
	const phaseIsSet = (props.data.typ === "il" || props.data.typ === "ih" || props.data.typ === "hi") && props.data.l !== 3;

	return <IonRow>
		<IonCol>{props.loc.alarm_name[props.data.typ] ?? `<${props.data.typ}>`}</IonCol>

		{props.showPhase && <IonCol size="1">{phaseIsSet ? props.data.l+1 : ""}</IonCol>}

		<IonCol>{(si ? FormatNumberSI(props.data.val, false, 2) : FormatNumber(props.data.val, 2))}{unit}</IonCol>

		<IonCol>{props.data.time}</IonCol>
	</IonRow>
}

const Content : FC = () => {
	const loc = useLoc(glob);
	const loader = useLoader("Alarms", null, { watchOthers: true });

	const dvc = useDeviceConnection().device;
	const showPhase = dvc._type === TYPE_GCRT;

	return <Page title={loc.title} loaders={[loader]} ignoreAlarms>
		<IonCard style={{overflowX: "auto", maxWidth: "calc(max(50%, 100em))"}}>
			{!loader.data?.Data.length
				? <IonItem lines="none">{loc.no_alarms}</IonItem>
				: <IonGrid class="table tableb" style={{minWidth: "54em"}}>
					<IonRow class="header bheader">
						<IonCol>{loc.typ}</IonCol>
						{showPhase && <IonCol size="1">{loc.phase}</IonCol>}
						<IonCol>{loc.val}</IonCol>
						<IonCol>{loc.time}</IonCol>
					</IonRow>

					{loader.data.Data.map((v, i) => <AlarmDisplay key={i} data={v} loc={loc} showPhase={showPhase} />)}
				</IonGrid>
			}
		</IonCard>
	</Page>
}


const glob = Glob({
	en: {
		title: "Alarms",
		no_alarms: "No alarms",

		alarm_name: {
			ul : "Undervoltage",
			uh : "Overvoltage",
			il : "Undercurrent",
			ih : "Overcurrent",
			co : "Power factor",
			hu : "Harmonics U",
			hi : "Harmonics I",
			te : "Temperature",
			rs : "Stage operations",
		},

		typ:   "Type",
		phase: "Phase",
		val:   "Value",
		time:  "Time",
	},
	cs: {
		title: "Alarmy",
		no_alarms: "Žádné alarmy",

		alarm_name: {
			ul : "Podpětí",
			uh : "Přepětí",
			il : "Podproud",
			ih : "Nadproud",
			co : "Účiník",
			hu : "Harmonické U",
			hi : "Harmonické I",
			te : "Teplota",
			rs : "Sepnutí stupně",
		},

		typ:   "Typ",
		phase: "Fáze",
		val:   "Hodnota",
		time:  "Čas",
	},
	pg: {
		title: "Alarmes",
		no_alarms: "Sen alarmes",

		alarm_name: {
			ul : "Subtensão",
			uh : "Sobretensão",
			il : "Subcorrente",
			ih : "Sobrecorrente",
			co : "Fator de potencia",
			hu : "Harmônicas V",
			hi : "Harmônicas I",
			te : "Temperatura",
			rs : "Operações em estágio",
		},

		typ:   "Tipo",
		phase: "Fase",
		val:   "Valor",
		time:  "Hora",
	},
	es: {
		title: "Alarmas",
		no_alarms: "Sin alarmas",

		alarm_name: {
			ul : "Subtensión",
			uh : "Sobretensión",
			il : "Subcorriente",
			ih : "Sobrecorriente",
			co : "Factor de potencia",
			hu : "Armonicas V",
			hi : "Armonicas I",
			te : "Temperatura",
			rs : "Operaciones de etapa",
		},

		typ:   "Tipo",
		phase: "Fase",
		val:   "Valor",
		time:  "Hora",
	}
} as const);


export const PAlarms : DvcPage = {
	page: Content,
	path: route,
	title: glob,
	icon: warningOutline,

	available(dvc) {
		return dvc._type === TYPE_GCR || dvc._type === TYPE_GCRT || dvc._type == TYPE_ICR;
	}
}

